import useTenantState from '@/states/tenant.state';
import { env } from '@/utils/constants/env';
import { getKeycloakInstance, loginWithKeycloak, logoutFromKeycloak, getAccessToken } from './keycloak-instance';

export interface KeycloakTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface KeycloakUserInfo {
  avatar_url: string | null | undefined;
  created_at: Date;
  email: string;
  id: number;
  name: string;
  tenant_role: string;
  updated_at: Date;
  trial_ends_at?: string | null;
}

/**
 * KeycloakAuthService now wraps keycloak-js library
 * This maintains backward compatibility while using the official client
 */
export class KeycloakAuthService {
  private static instance: KeycloakAuthService;

  private constructor() {} // eslint-disable-line @typescript-eslint/no-empty-function

  public static getInstance(): KeycloakAuthService {
    if (!KeycloakAuthService.instance) {
      KeycloakAuthService.instance = new KeycloakAuthService();
    }
    return KeycloakAuthService.instance;
  }

  /**
   * Login using keycloak-js
   * This replaces the manual PKCE flow
   */
  public async login(redirectUri?: string): Promise<void> {
    await loginWithKeycloak(redirectUri);
  }

  /**
   * Generate authorization URL (deprecated - use login() instead)
   * Kept for backward compatibility
   */
  public generateAuthUrl(codeVerifier: string, codeChallenge: string): string {
    console.warn('generateAuthUrl is deprecated, use login() instead');
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: env.keycloakClientId,
      redirect_uri: env.keycloakRedirectUri,
      scope: 'openid email profile',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      state: Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join(''),
    });

    return `${env.keycloakUrl}/realms/${env.keycloakRealm}/protocol/openid-connect/auth?${params}`;
  }

  /**
   * Exchange authorization code for tokens (deprecated)
   * keycloak-js handles this internally
   */
  public async exchangeCodeForTokens(
    code: string,
    codeVerifier: string,
  ): Promise<KeycloakTokenResponse> {
    console.warn('exchangeCodeForTokens is deprecated - keycloak-js handles this automatically');

    const tokenEndpoint = `${env.keycloakUrl}/realms/${env.keycloakRealm}/protocol/openid-connect/token`;

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: env.keycloakClientId,
      client_secret: env.keycloakClientSecret || '',
      code,
      redirect_uri: env.keycloakRedirectUri,
      code_verifier: codeVerifier,
    });

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Token exchange failed: ${response.status} - ${errorData}`);
    }

    return response.json();
  }

  /**
   * Refresh access token using keycloak-js
   */
  public async refreshAccessToken(refreshToken?: string): Promise<KeycloakTokenResponse> {
    const keycloak = getKeycloakInstance();

    try {
      const refreshed = await keycloak.updateToken(-1); // Force refresh

      if (refreshed && keycloak.token && keycloak.refreshToken) {
        return {
          access_token: keycloak.token,
          refresh_token: keycloak.refreshToken,
          token_type: 'Bearer',
          expires_in: keycloak.tokenParsed?.exp || 0,
        };
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      console.error('Failed to refresh token:', error);
      throw new Error(`Token refresh failed: ${error}`);
    }
  }

  /**
   * Get user information from backend API
   */
  public async getUserInfo(accessToken: string): Promise<KeycloakUserInfo> {
    const userInfoEndpoint = `${env.apiTasklistBaseUrl}/api/v1/authentication/profile`;

    const response = await fetch(userInfoEndpoint, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'x-active-tenant': String(useTenantState.getState().currentTenant?.slug ?? ''),
      },
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Failed to get user info: ${response.status} - ${errorData}`);
    }

    const result = await response.json();
    return result.data;
  }

  /**
   * Logout using keycloak-js
   */
  public async logout(
    refreshToken?: string,
    redirectUri: string = globalThis.location.origin + '/login',
  ): Promise<void> {
    await logoutFromKeycloak(redirectUri);
  }

  /**
   * Validate and decode JWT token
   */
  public decodeToken(token: string): any {
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Invalid token format');
      }

      const payload = JSON.parse(atob(tokenParts[1]));
      return payload;
    } catch (error) {
      console.error('Error decoding token:', error);
      throw error;
    }
  }

  /**
   * Check if token is expired
   */
  public isTokenExpired(token: string): boolean {
    try {
      const payload = this.decodeToken(token);
      const exp = payload.exp;

      if (typeof exp !== 'number') {
        return true;
      }

      // Add a small buffer (30 seconds) to prevent edge cases
      return (exp * 1000) < (Date.now() + 30000);
    } catch (error) {
      console.warn('Error checking token expiration:', error);
      return true;
    }
  }

  /**
   * Get current access token from keycloak-js instance
   */
  public async getValidToken(): Promise<string | undefined> {
    return await getAccessToken();
  }
}

export const keycloakAuth = KeycloakAuthService.getInstance();
